package link.erpango.quip.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.background
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.AlertDialog
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Scaffold
import androidx.compose.material.SnackbarHost
import androidx.compose.material.SnackbarHostState
import androidx.compose.material.Surface
import androidx.compose.material.Text
import androidx.compose.material.TextButton
import androidx.compose.material.rememberScaffoldState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Warning
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import coil3.compose.AsyncImage
import link.erpango.quip.domain.entities.DUser
import link.erpango.quip.features.myspareparts.ShareMySparePartUiState
import link.erpango.quip.features.myspareparts.ShareMySparePartViewModel
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import link.erpango.quip.ui.components.QuipPrimaryButton
import link.erpango.quip.ui.components.QuipTextButton
import link.erpango.quip.ui.components.QuipTextField
import link.erpango.quip.ui.components.QuipTransparentHeader
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.Res
import quip.composeapp.generated.resources.my_spare_part_share_already_shared_message
import quip.composeapp.generated.resources.my_spare_part_share_already_shared_title
import quip.composeapp.generated.resources.my_spare_part_share_error_generic
import quip.composeapp.generated.resources.my_spare_part_share_info
import quip.composeapp.generated.resources.my_spare_part_share_search_hint
import quip.composeapp.generated.resources.my_spare_part_share_success_message
import quip.composeapp.generated.resources.my_spare_part_share_success_title
import quip.composeapp.generated.resources.my_spare_part_share_title
import quip.composeapp.generated.resources.my_spare_part_share_users_empty
import quip.composeapp.generated.resources.my_spare_part_share_users_error_generic
import quip.composeapp.generated.resources.ok
import quip.composeapp.generated.resources.retry
import quip.composeapp.generated.resources.share

/**
 * Pantalla para compartir un repuesto personal con otro usuario.
 *
 * Compartir crea una copia independiente del repuesto en la cuenta destino (no hay
 * sincronización en vivo): el texto informativo lo deja explícito al usuario.
 */
@Composable
fun ShareMySparePartScreen(
    state: ShareMySparePartUiState,
    onSearchQueryChange: (String) -> Unit,
    onSelectUser: (DUser) -> Unit,
    onConfirmShareClick: () -> Unit,
    onLoadUsersRetry: () -> Unit,
    onShareSuccessDismissed: () -> Unit,
    onAlreadySharedDismissed: () -> Unit,
    onBackClick: () -> Unit
) {
    val snackbarHostState = remember { SnackbarHostState() }

    // Snackbar de errores: sentinel o mensaje crudo del backend.
    val errorSnackbarText = state.snackbarMessage?.let { raw ->
        when (raw) {
            ShareMySparePartViewModel.SNACKBAR_GENERIC_ERROR -> stringResource(Res.string.my_spare_part_share_error_generic)
            else -> raw
        }
    }

    LaunchedEffect(state.snackbarMessage) {
        errorSnackbarText?.let { snackbarHostState.showSnackbar(it) }
    }

    Scaffold(
        scaffoldState = rememberScaffoldState(snackbarHostState = snackbarHostState),
        snackbarHost = { SnackbarHost(snackbarHostState) },
        backgroundColor = QuipColors.colorBackground
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            QuipTransparentHeader {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onBackClick) {
                        Icon(
                            Icons.Default.ArrowBack,
                            contentDescription = stringResource(Res.string.my_spare_part_share_title),
                            tint = QuipColors.colorText
                        )
                    }
                    Spacer(modifier = Modifier.width(QuipDimens.padding8))
                    Text(
                        text = stringResource(Res.string.my_spare_part_share_title),
                        style = MaterialTheme.typography.h5,
                        fontWeight = FontWeight.Bold,
                        color = QuipColors.colorText
                    )
                }
            }

            when {
                state.isLoadingUsers -> Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }

                state.usersLoadError != null -> Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(horizontal = QuipDimens.padding24),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        Icons.Default.Warning,
                        contentDescription = null,
                        tint = QuipColors.colorError,
                        modifier = Modifier.size(QuipDimens.iconSize48)
                    )
                    Spacer(modifier = Modifier.height(QuipDimens.padding12))
                    Text(
                        text = state.usersLoadError.orEmpty().ifBlank { stringResource(Res.string.my_spare_part_share_users_error_generic) },
                        color = QuipColors.colorError,
                        style = MaterialTheme.typography.body1
                    )
                    Spacer(modifier = Modifier.height(QuipDimens.padding12))
                    QuipTextButton(
                        text = stringResource(Res.string.retry),
                        onClick = onLoadUsersRetry,
                        icon = Icons.Default.Refresh
                    )
                }

                else -> Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(horizontal = QuipDimens.padding16)
                ) {
                    QuipTextField(
                        value = state.searchQuery,
                        onValueChange = onSearchQueryChange,
                        label = stringResource(Res.string.my_spare_part_share_search_hint),
                        icon = Icons.Default.Search,
                        trailingIcon = if (state.searchQuery.isNotEmpty()) {
                            {
                                IconButton(onClick = { onSearchQueryChange("") }) {
                                    Icon(
                                        Icons.Default.Clear,
                                        contentDescription = stringResource(Res.string.my_spare_part_share_search_hint),
                                        tint = QuipColors.colorTextSecondary
                                    )
                                }
                            }
                        } else null
                    )

                    Spacer(modifier = Modifier.height(QuipDimens.padding12))

                    // Semántica de clonado: la copia es independiente, no hay sync en vivo.
                    Row(verticalAlignment = Alignment.Top) {
                        Icon(
                            Icons.Default.Info,
                            contentDescription = null,
                            tint = QuipColors.colorPrimary,
                            modifier = Modifier
                                .size(QuipDimens.iconSize20)
                                .padding(end = QuipDimens.padding8, top = QuipDimens.padding2)
                        )
                        Text(
                            text = stringResource(Res.string.my_spare_part_share_info),
                            style = MaterialTheme.typography.body2,
                            color = QuipColors.colorTextSecondary
                        )
                    }

                    Spacer(modifier = Modifier.height(QuipDimens.padding12))

                    Box(modifier = Modifier.weight(1f)) {
                        if (state.visibleUsers.isEmpty()) {
                            Column(
                                modifier = Modifier.fillMaxSize(),
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center
                            ) {
                                Text(
                                    text = stringResource(Res.string.my_spare_part_share_users_empty),
                                    style = MaterialTheme.typography.body1,
                                    color = QuipColors.colorTextSecondary
                                )
                            }
                        } else {
                            LazyColumn(
                                verticalArrangement = Arrangement.spacedBy(QuipDimens.padding8)
                            ) {
                                items(state.visibleUsers) { user ->
                                    ShareUserRow(
                                        user = user,
                                        selected = state.selectedUser == user,
                                        onClick = { onSelectUser(user) }
                                    )
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(QuipDimens.padding12))

                    if (state.isSharing) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(QuipDimens.buttonHeight),
                            contentAlignment = Alignment.Center
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(QuipDimens.padding8)
                            ) {
                                CircularProgressIndicator(modifier = Modifier.size(QuipDimens.iconSize24))
                                Text(
                                    text = stringResource(Res.string.share),
                                    style = MaterialTheme.typography.button,
                                    color = QuipColors.colorTextSecondary
                                )
                            }
                        }
                    } else {
                        QuipPrimaryButton(
                            text = stringResource(Res.string.share),
                            onClick = onConfirmShareClick,
                            enabled = state.selectedUser != null && state.sharedCopyId == null
                        )
                    }

                    Spacer(modifier = Modifier.height(QuipDimens.padding12))
                }
            }
        }
    }

    // Diálogo de éxito (201): muestra el ID de la copia creada. Al cerrarlo, App.kt vuelve a edición.
    if (state.sharedCopyId != null) {
        AlertDialog(
            onDismissRequest = onShareSuccessDismissed,
            title = {
                Text(
                    text = stringResource(Res.string.my_spare_part_share_success_title),
                    color = QuipColors.colorText,
                    fontWeight = FontWeight.Bold
                )
            },
            text = {
                Text(
                    text = stringResource(
                        Res.string.my_spare_part_share_success_message,
                        state.sharedCopyId!!,
                        displayName(state.sharedWithUser)
                    ),
                    color = QuipColors.colorTextSecondary
                )
            },
            confirmButton = {
                TextButton(onClick = onShareSuccessDismissed) {
                    Text(stringResource(Res.string.ok), color = QuipColors.colorPrimary)
                }
            },
            backgroundColor = Color.White,
            shape = RoundedCornerShape(QuipDimens.cornerRadius16)
        )
    }

    // Diálogo 409: el usuario destino ya tenía una copia (solo informativo).
    if (state.alreadySharedUser != null) {
        AlertDialog(
            onDismissRequest = onAlreadySharedDismissed,
            title = {
                Text(
                    text = stringResource(Res.string.my_spare_part_share_already_shared_title),
                    color = QuipColors.colorText,
                    fontWeight = FontWeight.Bold
                )
            },
            text = {
                Text(
                    text = stringResource(
                        Res.string.my_spare_part_share_already_shared_message,
                        displayName(state.alreadySharedUser)
                    ),
                    color = QuipColors.colorTextSecondary
                )
            },
            confirmButton = {
                TextButton(onClick = onAlreadySharedDismissed) {
                    Text(stringResource(Res.string.ok), color = QuipColors.colorPrimary)
                }
            },
            backgroundColor = Color.White,
            shape = RoundedCornerShape(QuipDimens.cornerRadius16)
        )
    }
}

/** Nombre para mostrar de un usuario: nombre si existe, email como fallback. */
private fun displayName(user: DUser?): String {
    if (user == null) return ""
    return user.name?.takeIf { it.isNotBlank() } ?: user.email.orEmpty()
}

/** Fila de usuario destino: avatar + nombre + email, con estado seleccionado. */
@Composable
private fun ShareUserRow(user: DUser, selected: Boolean, onClick: () -> Unit) {
    Surface(
        shape = RoundedCornerShape(QuipDimens.cornerRadius12),
        color = if (selected) QuipColors.colorPrimary.copy(alpha = 0.15f) else Color.White,
        elevation = QuipDimens.elevationSmall,
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = QuipDimens.padding12, vertical = QuipDimens.padding8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            UserAvatar(avatarUrl = user.avatar, name = displayName(user))
            Spacer(modifier = Modifier.width(QuipDimens.padding12))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = displayName(user),
                    style = MaterialTheme.typography.body1,
                    fontWeight = FontWeight.SemiBold,
                    color = QuipColors.colorText
                )
                if (!user.email.isNullOrBlank()) {
                    Text(
                        text = user.email.orEmpty(),
                        style = MaterialTheme.typography.caption,
                        color = QuipColors.colorTextSecondary
                    )
                }
            }
            if (selected) {
                Icon(
                    Icons.Default.Check,
                    contentDescription = null,
                    tint = QuipColors.colorPrimary
                )
            }
        }
    }
}

/** Avatar circular del usuario; si no hay URL de avatar muestra un icono genérico. */
@Composable
private fun UserAvatar(avatarUrl: String?, name: String) {
    Box(
        modifier = Modifier
            .size(QuipDimens.iconSize48)
            .clip(CircleShape)
            .background(QuipColors.colorPrimary.copy(alpha = 0.15f)),
        contentAlignment = Alignment.Center
    ) {
        if (avatarUrl.isNullOrBlank()) {
            Icon(
                Icons.Default.Person,
                contentDescription = name.ifBlank { null },
                tint = QuipColors.colorPrimary
            )
        } else {
            AsyncImage(
                model = avatarUrl,
                contentDescription = name.ifBlank { null },
                contentScale = ContentScale.Crop,
                modifier = Modifier
                    .fillMaxSize()
                    .clip(CircleShape)
            )
        }
    }
}
